/**
 * Read-only inspection of the access token the client is holding.
 *
 * The signature is the server's business — this exists purely so the app can
 * tell a session that has already aged out from one that is merely idle,
 * without spending a round trip to find out.
 */

// A token that dies in flight is already useless, so treat the last half-minute
// of its life as expired rather than racing it.
const CLOCK_SKEW_MS = 30_000;

const decodeClaims = (segment: string): Record<string, unknown> | null => {
    try {
        const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
        const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
        const bytes = atob(padded);
        const json = decodeURIComponent(
            Array.from(bytes, (c) => `%${c.charCodeAt(0).toString(16).padStart(2, "0")}`).join(""),
        );
        const claims = JSON.parse(json);
        return claims && typeof claims === "object" ? claims : null;
    } catch {
        return null;
    }
};

export const jwtExpiresAt = (token?: string | null): number | null => {
    const segment = token?.split(".")[1];
    if (!segment) return null;
    const exp = decodeClaims(segment)?.exp;
    return typeof exp === "number" ? exp * 1000 : null;
};

/**
 * Unreadable tokens and tokens without an `exp` count as live: the server is
 * the authority on validity, and guessing wrong here signs someone out of a
 * perfectly good session.
 */
export const isJwtExpired = (token?: string | null, now: number = Date.now()): boolean => {
    const expiresAt = jwtExpiresAt(token);
    return expiresAt !== null && expiresAt <= now + CLOCK_SKEW_MS;
};
